// Window "maximize / restore" host for panel-based pages (DCA analyzer + Type
// Curve). A lightweight context + a portal host div that lets any wrapped
// window (see MaximizablePanel) expand to fill the page's right-hand display
// area (`.page-content`) and shrink back — layered ON TOP of the existing
// react-resizable-panels split layout without disturbing it.
//
// Usage: place <MaximizeHost> INSIDE the `.page-content` element (which must be
// position:relative) and render the normal panel tree as its children. The host
// div it renders is an absolutely-positioned overlay (inset:0) that only covers
// `.page-content` — so the left nav + top breadcrumb stay visible when a window
// is maximized. Only ONE window may be maximized at a time; Esc restores.
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const MaximizeContext = createContext(null);

/** Hook for MaximizablePanel to read the maximize state + host ref. */
export function useMaximize() {
  return useContext(MaximizeContext);
}

/**
 * Provider + portal host. Renders its children (the normal panel tree) followed
 * by an overlay host div. When a window is maximized the host is shown and the
 * window's DOM is re-parented into it (see MaximizablePanel).
 */
export function MaximizeHost({ children }) {
  const [maximizedId, setMaximizedId] = useState(null);
  const hostRef = useRef(null);

  // Esc restores the maximized window.
  useEffect(() => {
    if (!maximizedId) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMaximizedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [maximizedId]);

  // On maximize / restore, nudge charts to reflow into the new box. echarts
  // (size-sensor) + Leaflet (trackResize) both listen for window 'resize', so a
  // single dispatch after the DOM re-parent forces an immediate, gap-free
  // redraw as a belt-and-suspenders on top of their own auto-detection.
  useEffect(() => {
    const t = setTimeout(
      () => window.dispatchEvent(new Event("resize")),
      60
    );
    return () => clearTimeout(t);
  }, [maximizedId]);

  const ctx = useMemo(
    () => ({ maximizedId, setMaximizedId, hostRef }),
    [maximizedId]
  );

  return (
    <MaximizeContext.Provider value={ctx}>
      {children}
      <div
        ref={hostRef}
        // Overlay that covers ONLY `.page-content` (its position:relative
        // ancestor). Hidden until a window is maximized; opaque dark bg so the
        // underlying split layout never bleeds through.
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1000,
          display: maximizedId ? "block" : "none",
          background: "#1a1f38",
        }}
      />
    </MaximizeContext.Provider>
  );
}

export default MaximizeHost;
